import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { motion } from "framer-motion";
import { Eye, EyeOff, Zap } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        if (!name.trim()) { setError("Name is required"); setLoading(false); return; }
        await signup(name, email, password);
      }
      navigate("/studio");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex page-enter" style={{ background: "var(--surface-page)" }}>
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-16 relative overflow-hidden">
        {/* Floating emotion particles */}
        {["#E8BFC4", "#BCC5D1", "#A69C8D", "#F2D9B0", "#C9BDD9", "#BFD3C6", "#D89A86", "#C9A977"].map((color, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full opacity-20"
            style={{
              width: Math.random() * 120 + 40,
              height: Math.random() * 120 + 40,
              background: `radial-gradient(circle, ${color}, transparent)`,
              left: `${Math.random() * 80}%`,
              top: `${Math.random() * 80}%`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, 15, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 4 + i * 0.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
          />
        ))}

        <div className="relative z-10 max-w-md">
            <h1 className="font-display text-4xl font-semibold text-[var(--ink-primary)] leading-tight mb-4">
              Stories that move.
            </h1>
            <p className="text-[var(--ink-secondary)] text-lg leading-relaxed max-w-md">
              ArcScript's emotional engine turns your ideas into scenes that audiences feel.
            </p>
          </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black"
              style={{ background: "linear-gradient(135deg, var(--accent-gold), var(--accent-gold))" }}>A</div>
            <span className="font-black text-lg">ArcScript AI</span>
          </div>

          <div className="rounded-2xl p-8" style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
            {/* Toggle */}
            <div className="flex rounded-xl p-1 mb-8" style={{ background: "var(--surface-page)" }}>
              {["login", "signup"].map(m => (
                <button
                  key={m}
                  onClick={() => { setMode(m); setError(""); }}
                  data-testid={`auth-tab-${m}`}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
                  style={mode === m ? { background: "var(--accent-gold)", color: "var(--ink-inverse)" } : { color: "var(--ink-tertiary)" }}
                >
                  {m === "login" ? "Sign In" : "Create Account"}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "signup" && (
                <div>
                  <label className="block text-[var(--ink-secondary)] text-sm mb-1.5">Your Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Jane Doe"
                    data-testid="auth-name-input"
                    className="w-full px-4 py-3 rounded-xl placeholder-[var(--ink-tertiary)] text-sm outline-none transition-colors"
                    style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }}
                    onFocus={e => e.target.style.borderColor = "var(--accent-gold)"}
                    onBlur={e => e.target.style.borderColor = "var(--border-subtle)"}
                  />
                </div>
              )}

              <div>
                <label className="block text-[var(--ink-secondary)] text-sm mb-1.5">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="writer@studio.com"
                  required
                  data-testid="auth-email-input"
                  className="w-full px-4 py-3 rounded-xl placeholder-[var(--ink-tertiary)] text-sm outline-none transition-colors"
                  style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }}
                  onFocus={e => e.target.style.borderColor = "var(--accent-gold)"}
                  onBlur={e => e.target.style.borderColor = "var(--border-subtle)"}
                />
              </div>

              <div>
                <label className="block text-[var(--ink-secondary)] text-sm mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="auth-password-input"
                    className="w-full px-4 py-3 rounded-xl placeholder-[var(--ink-tertiary)] text-sm outline-none transition-colors pr-11"
                    style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }}
                    onFocus={e => e.target.style.borderColor = "var(--accent-gold)"}
                    onBlur={e => e.target.style.borderColor = "var(--border-subtle)"}
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ink-tertiary)] hover:text-[var(--ink-secondary)]">
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {mode === "login" && (
                  <div className="flex justify-end mt-2">
                    <Link
                      to="/forgot-password"
                      data-testid="forgot-password-link"
                      className="text-xs font-medium text-[var(--accent-gold)] hover:text-[var(--accent-gold-hover)] transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>
                )}
              </div>

              {error && (
                <div className="rounded-xl px-4 py-3 text-sm" style={{ background: "rgba(184, 117, 107, 0.1)", border: "1px solid rgba(184, 117, 107, 0.3)", color: "var(--ink-primary)" }}
                  data-testid="auth-error">
                  {error}
                </div>
              )}

              <motion.button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-button"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
                style={{ background: loading ? "var(--surface-card)" : "var(--accent-gold)", border: loading ? "1px solid var(--border-subtle)" : "none" }}
              >
                {loading ? (
                  <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  <>
                    <Zap size={16} />
                    {mode === "login" ? "Enter the Studio" : "Start Creating"}
                  </>
                )}
              </motion.button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
